import { useEffect, useRef, useState } from 'react';
import { GAME_RULES } from '@continuum/show-core';
import type { RhythmTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono } from '../ui/atoms.js';
import { ERA_SKIN, EraBackdrop, PulseSprite } from './art.js';
import { encodeRhythmFrame } from './frames.js';

/** Closer than this and two coils arrive too close for one relaxed thumb to take both. */
const MIN_SEPARATION_MS = 450;

interface Pulse {
  id: number;
  bornAt: number;
  /** Which coil it is coming down. */
  lane: number;
  /** How long this one takes to reach the line. */
  travel: number;
  hit: boolean;
  silent: boolean;
}

/**
 * ENGINEER — pulses run down the coils; tap each one as it crosses the line.
 *
 * The pulses come down two coils, each at its own speed, so they cross out of step with
 * one another. Slow and forgiving since the test evening (2026-09-28): a wide window at
 * the line (GAME_RULES.rhythm), and never two pulses at once. The drive could stutter
 * and drop pulses; no era asks for that any more.
 */
export function Rhythm({ task, era, onProgress, onWin, onMistake, accent, onFrame, paused = false, s }: GameProps<RhythmTask>) {
  const skin = ERA_SKIN[era];
  const { hitWindowMs, travelMs } = GAME_RULES.rhythm;
  const beatMs = 60_000 / task.bpm;
  const need = task.target;

  const [pulses, setPulses] = useState<Pulse[]>([]);
  const [hits, setHits] = useState(0);
  const [flash, setFlash] = useState<'hit' | 'miss' | null>(null);
  const [, tick] = useState(0);
  const beat = useRef(0);
  /** Which coils we fed recently — a longer memory than the pulses in the air. */
  const recentLanes = useRef<number[]>([]);
  const pulsesRef = useRef<Pulse[]>([]);
  const won = useRef(false);
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake });
  cbs.current = { onProgress, onWin, onFrame, onMistake };

  // The game's own clock, which stops while something covers the board: pulses hang where they
  // are, and pick up exactly there when the board is back.
  const hold = useRef(paused);
  hold.current = paused;
  const stoppedAt = useRef<number | null>(null);
  const lost = useRef(0);
  const clock = () => (stoppedAt.current ?? Date.now()) - lost.current;
  useEffect(() => {
    if (paused && stoppedAt.current === null) stoppedAt.current = Date.now();
    if (!paused && stoppedAt.current !== null) {
      lost.current += Date.now() - stoppedAt.current;
      stoppedAt.current = null;
    }
  }, [paused]);

  useEffect(() => {
    const spawn = setInterval(() => {
      if (hold.current) return;
      const step = beat.current % task.pattern.length;
      beat.current += 1;
      if (!task.pattern[step]) return;
      // Every fourth bar the drive misses one, if it is failing.
      const silent = task.stutter && beat.current % 7 === 0;
      // Decide the coil here, not inside the state updater: the updater only sees
      // pulses still in the air, and at slow tempos that is a single pulse — far too
      // short a memory to rotate three coils fairly. `recentLanes` is that memory.
      const at = clock();
      const inAir = pulsesRef.current.filter((p) => at - p.bornAt < p.travel + 700);
      const lastLane = recentLanes.current.at(-1) ?? -1;

      const options = Array.from({ length: task.lanes }, (_, l) => {
        const arrival = at + travelMs * (task.laneSpeeds[l] ?? 1);
        // How close this coil's arrival would land to one already on its way down.
        const sep = inAir.reduce(
          (min, p) => Math.min(min, Math.abs(p.bornAt + p.travel - arrival)),
          Number.POSITIVE_INFINITY,
        );
        const used = recentLanes.current.filter((r) => r === l).length;
        return { lane: l, sep, used };
      });

      // Rule 1: never land on top of a pulse already in the air — two coils crossing
      // 20ms apart is a two-thumb chord nobody can play. If every coil collides, take
      // the roomiest rather than stacking them.
      const clear = options.filter((o) => o.sep >= MIN_SEPARATION_MS);
      const pool =
        clear.length > 0 ? clear : [...options].sort((a, b) => b.sep - a.sep).slice(0, 1);
      // Rule 2: of the coils that clear rule 1, feed the one used least lately, and
      // step off the coil we just fed. Keeps all three live without a metronomic sweep.
      const chosen = pool.reduce((best, o) => {
        const score = -o.used * 10 - (o.lane === lastLane ? 5 : 0);
        const bestScore = -best.used * 10 - (best.lane === lastLane ? 5 : 0);
        return score > bestScore ? o : best;
      }, pool[0]!);

      const lane = chosen.lane;
      recentLanes.current = [...recentLanes.current, lane].slice(-task.lanes * 2);
      const travel = travelMs * (task.laneSpeeds[lane] ?? 1);
      setPulses((prev) => [
        ...prev.filter((p) => at - p.bornAt < p.travel + 700),
        { id: beat.current, bornAt: at, lane, travel, hit: false, silent },
      ]);
    }, beatMs);
    const frame = setInterval(() => {
      tick((n) => n + 1);
      const at = clock();
      cbs.current.onFrame(
        encodeRhythmFrame(
          pulsesRef.current
            .filter((p) => !p.hit)
            .map((p) => ({ lane: p.lane, t: (at - p.bornAt) / p.travel })),
        ),
      );
    }, 33);
    return () => {
      clearInterval(spawn);
      clearInterval(frame);
    };
  }, [beatMs, travelMs, task.pattern, task.stutter, task.lanes, task.laneSpeeds]);

  const tap = (lane: number) => {
    if (hold.current) return;
    pulsesRef.current = pulses;
    const now = clock();
    // only this coil's pulses — a tap on coil 2 cannot rescue a pulse on coil 1
    const live = pulses.filter((p) => !p.hit && p.lane === lane);
    const target = live.find((p) => Math.abs(now - p.bornAt - p.travel) < hitWindowMs);
    if (!target) {
      cbs.current.onMistake?.();
      setFlash('miss');
      setTimeout(() => setFlash(null), 180);
      return;
    }
    setPulses((prev) => prev.map((p) => (p.id === target.id ? { ...p, hit: true } : p)));
    setFlash('hit');
    setTimeout(() => setFlash(null), 140);
    setHits((h) => {
      const next = h + 1;
      cbs.current.onProgress(Math.min(100, Math.round((next / need) * 100)));
      if (next >= need && !won.current) {
        won.current = true;
        // Not inside the state update: WELL DONE is another component's state.
        queueMicrotask(() => cbs.current.onWin());
      }
      return next;
    });
  };

  pulsesRef.current = pulses;
  const now = clock();

  // Which side is next: the first light still to reach the line. Its pad wears the arrow, and
  // each pad warms up slowly as its own light comes down (a ramp, never a flash).
  const waiting = pulses
    .filter((p) => !p.hit && !p.silent && now - p.bornAt - p.travel < hitWindowMs)
    .sort((a, b) => a.bornAt + a.travel - (b.bornAt + b.travel));
  const nextLane = waiting[0]?.lane ?? null;
  const warmth = (lane: number) =>
    pulses
      .filter((p) => p.lane === lane && !p.hit && !p.silent)
      .reduce((max, p) => {
        const t = (now - p.bornAt) / p.travel;
        if (t > 1 + hitWindowMs / p.travel) return max;
        return Math.max(max, Math.min(1, Math.max(0, (t - 0.5) / 0.5)));
      }, 0);

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontFamily: mono, fontSize: 15, fontWeight: 700, letterSpacing: 1.5, color: accent }}>
          {s.rhythmHits(hits, need)}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: mono, fontSize: 13, letterSpacing: 1.2, color: '#dfe8ee' }}>{s.rhythmThumbs}</span>
      </div>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 300,
          borderRadius: 16,
          border: `1px solid ${flash === 'miss' ? '#ff2e3f' : `${accent}33`}`,
          background: flash === 'hit' ? `${accent}14` : '#0a0e14',
          overflow: 'hidden',
          display: 'flex',
          gap: 8,
          padding: 8,
          transition: 'background .1s, border-color .1s',
        }}
      >
        <EraBackdrop era={era} opacity={0.32} />

        {Array.from({ length: task.lanes }, (_, lane) => {
          const warm = warmth(lane);
          const isNext = nextLane === lane;
          const side = s.rhythmSide(lane, task.lanes);
          return (
            <button
              key={lane}
              data-nopress
              aria-label={side}
              onPointerDown={() => tap(lane)}
              style={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                borderRadius: 12,
                border: `2px solid ${isNext ? accent : `${accent}40`}`,
                background: 'rgba(8,12,18,.72)',
                padding: 0,
                overflow: 'hidden',
                touchAction: 'none',
                transition: 'border-color .25s',
              }}
            >
              {/* the pad warms as its light comes down */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(to bottom, transparent 30%, ${accent})`,
                  opacity: warm * 0.34,
                  pointerEvents: 'none',
                }}
              />
              {/* the line this side's lights have to reach */}
              <div
                style={{
                  position: 'absolute',
                  left: 6,
                  right: 6,
                  bottom: 70,
                  height: 3,
                  borderRadius: 2,
                  background: accent,
                  opacity: 0.55 + warm * 0.45,
                  boxShadow: `0 0 ${6 + warm * 12}px ${accent}`,
                }}
              />

              {pulses
                .filter((p) => p.lane === lane)
                .map((p) => {
                  const t = Math.min(1.25, (now - p.bornAt) / p.travel);
                  // 0 at the top of the pad, 1 on the line (70 px up from the bottom of 284).
                  const top = `calc(${t} * (100% - 88px))`;
                  if (p.silent) {
                    return (
                      <div
                        key={p.id}
                        style={{ position: 'absolute', left: '50%', top, transform: 'translateX(-50%)', width: 36, height: 36 }}
                      >
                        <PulseSprite era={era} silent />
                      </div>
                    );
                  }
                  return (
                    <div
                      key={p.id}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top,
                        transform: `translateX(-50%) scale(${p.hit ? 1.6 : 1})`,
                        width: 36,
                        height: 36,
                        opacity: p.hit ? 0 : 1,
                        transition: 'opacity .18s, transform .18s',
                        filter: `drop-shadow(0 0 8px ${skin.glow})`,
                      }}
                    >
                      <PulseSprite era={era} />
                    </div>
                  );
                })}

              {/* the side's name, big, under the line; the next side wears the arrow */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  fontFamily: mono,
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    fontSize: 22,
                    lineHeight: 1,
                    color: accent,
                    opacity: isNext ? 1 : 0,
                    transition: 'opacity .2s',
                  }}
                >
                  ▲
                </span>
                <span
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    letterSpacing: 2.5,
                    color: isNext ? '#ffffff' : '#7d8e9b',
                    transition: 'color .2s',
                  }}
                >
                  {side}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {task.stutter && (
        <div
          style={{
            marginTop: 8,
            textAlign: 'center',
            fontFamily: mono,
            fontSize: 12,
            letterSpacing: 1,
            color: '#ff9a4a',
          }}
        >
          {s.rhythmFaint}
        </div>
      )}
      <div
        style={{
          marginTop: 8,
          textAlign: 'center',
          fontFamily: mono,
          fontSize: 13,
          letterSpacing: 0.5,
          lineHeight: 1.4,
          color: '#9db3c2',
        }}
      >
        {s.rhythmTap}
      </div>
    </div>
  );
}
